/**
 * `useShares` — client-side hook for the share-management admin API
 * (`/api/admin/shares`, issue #8). Mirrors `usePendingSourceActions`:
 *
 *   - `shares`: the list of provisioned shares (one per shared deck).
 *   - `isLoading`: true until the first fetch resolves (success OR failure).
 *   - `createShare(input)`: `POST /api/admin/shares`; resolves the new record,
 *     throws an Error carrying the server's message on a non-OK response (so
 *     the dialog can surface 400 / 409 / 503 inline).
 *   - `updateShare(host, allowlist)`: `PUT /api/admin/shares/<host>`.
 *   - `deleteShare(host)`: `DELETE /api/admin/shares/<host>`.
 *
 * The initial GET reads work even when provisioning is unconfigured (the
 * Worker only gates the WRITE paths with 503). Writes go through
 * `adminWriteHeaders()` so the dev placeholder Access email is injected under
 * `wrangler dev`.
 */

import { useCallback, useEffect, useState } from "react";
import type { CreateShareInput, ShareRecord, UpdateShareInput } from "./shares";
import { adminWriteHeaders } from "./admin-fetch";

const BASE = "/api/admin/shares";

interface SharesResponse {
  shares: ShareRecord[];
}

/** Pull the server's `{ error }` message off a non-OK response, with a fallback. */
async function errorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) return body.error;
  } catch {
    /* not JSON — use the fallback */
  }
  return fallback;
}

export interface UseSharesResult {
  shares: ShareRecord[];
  isLoading: boolean;
  /**
   * Create a share. `POST /api/admin/shares`. Resolves the new record and
   * adds it to the list; throws an Error carrying the server's message on a
   * non-OK response (400 invalid, 409 conflict, 503 unconfigured).
   */
  createShare: (input: CreateShareInput) => Promise<ShareRecord>;
  /**
   * Delete a share. `DELETE /api/admin/shares/<host>`. Removes it from the
   * list on success; throws the server's message on a non-OK response.
   */
  deleteShare: (host: string) => Promise<void>;
  /**
   * Replace a share's allowlist. `PUT /api/admin/shares/<host>`. Updates the
   * record in the list on success; throws the server's message on a non-OK
   * response.
   */
  updateShare: (host: string, allowlist: UpdateShareInput) => Promise<ShareRecord>;
}

export function useShares(): UseSharesResult {
  const [shares, setShares] = useState<ShareRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchShares = useCallback(async (): Promise<void> => {
    try {
      const res = await fetch(BASE, {
        cache: "no-store",
        headers: adminWriteHeaders(),
      });
      if (!res.ok) {
        setShares([]);
        return;
      }
      const body = (await res.json()) as SharesResponse;
      setShares(Array.isArray(body.shares) ? body.shares : []);
    } catch {
      // Best-effort: a transient blip keeps the last-seen list.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchShares();
      if (!cancelled) setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchShares]);

  const createShare = useCallback(
    async (input: CreateShareInput): Promise<ShareRecord> => {
      const res = await fetch(BASE, {
        method: "POST",
        headers: adminWriteHeaders(),
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        throw new Error(
          await errorMessage(res, `Failed to create share (${res.status})`),
        );
      }
      const record = (await res.json()) as ShareRecord;
      setShares((prev) => [
        record,
        ...prev.filter((s) => s.host !== record.host),
      ]);
      return record;
    },
    [],
  );

  const deleteShare = useCallback(async (host: string): Promise<void> => {
    const res = await fetch(`${BASE}/${encodeURIComponent(host)}`, {
      method: "DELETE",
      headers: adminWriteHeaders(),
    });
    if (!res.ok) {
      throw new Error(
        await errorMessage(res, `Failed to delete share (${res.status})`),
      );
    }
    setShares((prev) => prev.filter((s) => s.host !== host));
  }, []);

  const updateShare = useCallback(
    async (host: string, allowlist: UpdateShareInput): Promise<ShareRecord> => {
      const res = await fetch(`${BASE}/${encodeURIComponent(host)}`, {
        method: "PUT",
        headers: adminWriteHeaders(),
        body: JSON.stringify(allowlist),
      });
      if (!res.ok) {
        throw new Error(
          await errorMessage(res, `Failed to update share (${res.status})`),
        );
      }
      const record = (await res.json()) as ShareRecord;
      setShares((prev) => prev.map((s) => (s.host === host ? record : s)));
      return record;
    },
    [],
  );

  return { shares, isLoading, createShare, deleteShare, updateShare };
}

// Re-exported so consumers can import input/record types from one place.
export type { CreateShareInput, ShareRecord, UpdateShareInput };
